import { VIDEO_QUALITY_PRESETS } from '../constants';

export type QualityLevel = keyof typeof VIDEO_QUALITY_PRESETS;

/**
 * Network condition assessment based on connection quality
 */
export interface NetworkCondition {
  connectionQuality: 'excellent' | 'good' | 'poor' | 'unknown';
  bandwidth?: number; // in Mbps
  latency?: number; // in ms
}

/**
 * Determine optimal video quality based on network conditions
 */
export function getOptimalQuality(networkCondition: NetworkCondition): QualityLevel {
  switch (networkCondition.connectionQuality) {
    case 'excellent':
      return 'HIGH';
    case 'good':
      return 'MEDIUM';
    case 'poor':
    case 'unknown':
    default:
      return 'LOW';
  }
}

/**
 * Get quality preset for a specific quality level
 */
export function getQualityPreset(quality: QualityLevel) {
  return VIDEO_QUALITY_PRESETS[quality];
}

/**
 * Check if device supports high resolution video
 */
export async function checkDeviceCapabilities(): Promise<{
  supports4K: boolean;
  supports1080p: boolean;
  maxWidth: number;
  maxHeight: number;
}> {
  try {
    // Get device capabilities
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    if (videoDevices.length === 0) {
      return {
        supports4K: false,
        supports1080p: false,
        maxWidth: 1280,
        maxHeight: 720,
      };
    }

    // Test with high resolution constraints
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 3840, max: 3840 },
        height: { ideal: 2160, max: 2160 },
        frameRate: { ideal: 30, max: 60 },
      },
    });

    const videoTrack = stream.getVideoTracks()[0];
    const settings = videoTrack.getSettings();

    // Stop the stream
    stream.getTracks().forEach(track => track.stop());

    const maxWidth = settings.width || 1280;
    const maxHeight = settings.height || 720;

    return {
      supports4K: maxWidth >= 3840 && maxHeight >= 2160,
      supports1080p: maxWidth >= 1920 && maxHeight >= 1080,
      maxWidth,
      maxHeight,
    };
  } catch (error) {
    console.warn('Could not check device capabilities:', error);
    return {
      supports4K: false,
      supports1080p: false,
      maxWidth: 1280,
      maxHeight: 720,
    };
  }
}

/**
 * Adaptive quality manager for live streaming
 */
export class AdaptiveQualityManager {
  private currentQuality: QualityLevel = 'MEDIUM';
  private networkCondition: NetworkCondition = { connectionQuality: 'unknown' };
  private deviceCapabilities: Awaited<ReturnType<typeof checkDeviceCapabilities>> | null = null;

  constructor() {
    this.initializeDeviceCapabilities();
  }

  private async initializeDeviceCapabilities() {
    this.deviceCapabilities = await checkDeviceCapabilities();
  }

  /**
   * Update network condition and adjust quality accordingly
   */
  updateNetworkCondition(condition: NetworkCondition) {
    this.networkCondition = condition;
    const optimalQuality = getOptimalQuality(condition);

    // Only downgrade quality if device supports it, don't upgrade beyond device capabilities
    if (this.deviceCapabilities) {
      if (optimalQuality === 'HIGH' && !this.deviceCapabilities.supports4K) {
        this.currentQuality = this.deviceCapabilities.supports1080p ? 'MEDIUM' : 'LOW';
      } else if (optimalQuality === 'MEDIUM' && !this.deviceCapabilities.supports1080p) {
        this.currentQuality = 'LOW';
      } else {
        this.currentQuality = optimalQuality;
      }
    } else {
      this.currentQuality = optimalQuality;
    }
  }

  /**
   * Get current quality level
   */
  getCurrentQuality(): QualityLevel {
    return this.currentQuality;
  }

  /**
   * Get current quality preset
   */
  getCurrentPreset() {
    return getQualityPreset(this.currentQuality);
  }

  /**
   * Force set quality level (useful for manual override)
   */
  setQuality(quality: QualityLevel) {
    if (this.deviceCapabilities) {
      if (quality === 'HIGH' && !this.deviceCapabilities.supports4K) {
        console.warn('Device does not support 4K, falling back to 1080p');
        this.currentQuality = this.deviceCapabilities.supports1080p ? 'MEDIUM' : 'LOW';
      } else if (quality === 'MEDIUM' && !this.deviceCapabilities.supports1080p) {
        console.warn('Device does not support 1080p, falling back to 720p');
        this.currentQuality = 'LOW';
      } else {
        this.currentQuality = quality;
      }
    } else {
      this.currentQuality = quality;
    }
  }

  /**
   * Get device capabilities
   */
  getDeviceCapabilities() {
    return this.deviceCapabilities;
  }
}

/**
 * Create a singleton instance of the adaptive quality manager
 */
export const adaptiveQualityManager = new AdaptiveQualityManager();
